import {Select as ChakraSelect, Field, Portal, SelectRootProps, Text, createListCollection} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useMemo, useRef} from 'react'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {type GetPageQuery, type GetSingleQuery, useZeroVirtualizer} from '../../utils/zero-virtualizer/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'
import {useSelectField} from './use-select-field.js'
import {BaseRowData} from '../../types/index.js'

interface Props<TRow extends {id?: string | null}, TStartRow, TListContextParams> extends Omit<
  SelectRootProps,
  'collection'
> {
  transformToSelectItem: (row: TRow) => SelectItem
  listHeight?: number
  estimateItemHeight?: number

  getPageQuery: GetPageQuery<TRow, TStartRow>
  getSingleQuery: GetSingleQuery<TRow>
  toStartRow: (row: TRow) => TStartRow
  listContextParams: TListContextParams

  label: string
  hideLabel?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  controlProps?: ChakraSelect.ControlProps
  contentProps?: ChakraSelect.ContentProps
  portalled?: boolean
  displayClearTrigger?: boolean
  errorMessagePadding?: number
}

export function InfiniteSelect<
  TRow extends BaseRowData,
  TStartRow extends BaseRowData,
  TListContextParams extends {limit: number},
>({
  transformToSelectItem,
  listHeight = 20 * 16,
  estimateItemHeight = 32,
  getPageQuery,
  getSingleQuery,
  toStartRow,
  listContextParams,
  label,
  hideLabel,
  placeholder,
  required,
  children,
  controlProps,
  contentProps,
  description,
  helpText,
  displayClearTrigger,
  errorMessagePadding,
  portalled = true,
  ...props
}: Props<TRow, TStartRow, TListContextParams>) {
  const {t} = useTranslate('common')
  const {field, value, handleChange} = useSelectField()
  const scrollRef = useRef<HTMLDivElement>(null)

  const {virtualizer, rowAt, complete, rowsEmpty} = useZeroVirtualizer<
    HTMLDivElement,
    Element,
    TListContextParams,
    TRow,
    TStartRow
  >({
    estimateSize: () => estimateItemHeight,
    overscan: 5,
    getScrollElement: () => scrollRef.current,
    listContextParams,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    getRowKey: row => row.id ?? '',
  })

  const virtualItems = virtualizer.getVirtualItems()

  const {collection, rowToItem} = useMemo(() => {
    const itemMap = new Map<string, SelectItem>()
    for (const vItem of virtualItems) {
      const row = rowAt(vItem.index)
      if (row?.id) {
        const item = transformToSelectItem(row)
        itemMap.set(row.id, item)
      }
    }
    return {
      collection: createListCollection({items: Array.from(itemMap.values())}),
      rowToItem: (row: TRow | undefined) => {
        if (!row?.id) return undefined
        return itemMap.get(row.id)
      },
    }
  }, [virtualItems, rowAt, transformToSelectItem])

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraSelect.Root
        collection={collection}
        name={field.name}
        value={value}
        onValueChange={e => handleChange(e.value)}
        onBlur={field.handleBlur}
        {...props}
      >
        <ChakraSelect.HiddenSelect />

        <Field.Label asChild>
          <ChakraSelect.Label srOnly={hideLabel}>
            {label} {required && <FieldRequiredIndicator />}
          </ChakraSelect.Label>
        </Field.Label>

        {helpText && <Field.HelperText srOnly={hideLabel}>{helpText}</Field.HelperText>}

        {!children && (
          <ChakraSelect.Control backgroundColor="bg" {...controlProps}>
            <ChakraSelect.Trigger>
              <ChakraSelect.ValueText placeholder={placeholder} />
            </ChakraSelect.Trigger>
            <ChakraSelect.IndicatorGroup>
              {displayClearTrigger && <ChakraSelect.ClearTrigger />}
              <ChakraSelect.Indicator />
            </ChakraSelect.IndicatorGroup>
          </ChakraSelect.Control>
        )}

        {description && (
          <Field.HelperText mt={2} srOnly={description}>
            {description}
          </Field.HelperText>
        )}

        {children && (
          <ChakraSelect.Control {...controlProps}>
            <ChakraSelect.Trigger asChild unstyled>
              {children}
            </ChakraSelect.Trigger>
          </ChakraSelect.Control>
        )}

        <Portal disabled={!portalled}>
          <ChakraSelect.Positioner>
            <ChakraSelect.Content
              ref={scrollRef}
              minWidth="20rem"
              maxWidth="20rem"
              height={`${listHeight}px`}
              overflow="auto"
              {...contentProps}
            >
              {rowsEmpty && complete ? (
                <Text padding="1" fontSize="xs" color="fg.muted">
                  {t('no_options_available')}
                </Text>
              ) : (
                <div style={{height: `${virtualizer.getTotalSize()}px`, position: 'relative'}}>
                  {virtualItems.map(virtualRow => {
                    const row = rowAt(virtualRow.index)
                    const item = rowToItem(row)
                    if (!item) {
                      return (
                        <div
                          key={virtualRow.key}
                          data-index={virtualRow.index}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${estimateItemHeight}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        />
                      )
                    }
                    return (
                      <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${estimateItemHeight}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                        title={item.label}
                      >
                        <ChakraSelect.Item item={item} truncate height="100%">
                          {item.label}
                          <ChakraSelect.ItemIndicator />
                        </ChakraSelect.Item>
                      </div>
                    )
                  })}
                </div>
              )}
            </ChakraSelect.Content>
          </ChakraSelect.Positioner>
        </Portal>
      </ChakraSelect.Root>

      <ErrorMessage errorMessagePadding={errorMessagePadding} />
    </FieldRoot>
  )
}
